<script lang="ts" module>
  import type { SystemStyleObject } from '@agapi/ui/panda/types';
  import type { ProductVariant } from '@agapi/mlc-core';

  import type { CartStore } from '../../../stores/cart';

  export interface ProductCardProps {
    product: ProductVariant;
    buttonLabel: string;
    imageWidth?: number;
    /* Onclick for any of the onclick events */
    onclick?: (ev?: MouseEvent) => void;
    /* Callback for when the product is added to the cart */
    onProductAdded?: (ev?: MouseEvent) => void;
    /* If true, the cart drawer will not be opened */
    ignoreDrawer?: boolean;
    css?: SystemStyleObject;
    linkStyles?: SystemStyleObject;
    imageAspectRatio?: number;
    titleStyles?: SystemStyleObject;
  }
</script>

<script lang="ts">
  import { page } from '$app/state';

  import { getContext } from 'svelte';
  import { ShoppingCartSimple, Check, X, Bell } from 'phosphor-svelte';
  import { css } from '@agapi/ui/panda/css';
  import { float } from '@agapi/ui/panda/patterns';
  import { Button } from '@agapi/ui/button';
  import { Card, CardImage, CardHeader, CardContent, CardFooter } from '@agapi/ui/card';
  import { Image } from '@agapi/ui/image';
  import { Loader } from '@agapi/ui/loader';

  import { productCardStyles } from '../product-card.styles';

  import CampaignBadge from '../../campaign-badge/campaign-badge.svelte';
  import ShortDatedBadge from '../../short-dated-badge/short-dated-badge.svelte';
  import BackInStockDialog from '../back-in-stock-dialog.svelte';
  import { EVENTS } from '../../../services';
  import { calculatePercentDiscount } from '../../../utils/pricing.server';
  import { variantToCartItem } from '../../../utils/transformer.client';
  import { getLocaleFromUrl, getLocaleFormattedPrice, rewriteUrl } from '../../../utils';

  import * as m from '$lib/parachute/messages';

  const { add } = getContext<CartStore>('cart-store');

  const {
    product,
    css: cssProp,
    buttonLabel,
    onclick,
    onProductAdded,
    imageAspectRatio = 1/1,
    ignoreDrawer,
    imageWidth = 295,
    titleStyles,
    ...props
  }: ProductCardProps = $props();

  let added = $state(false);
  let loading = $state(false);
  let outOfStock = $state(product.stock <= 0);
  let showPopover = $state(false);
  let showBackInStockDialog = $state(false);

  const classes = productCardStyles.raw();

  let buttonRef: HTMLSpanElement | null = null;

  const triggerButtonShake = () => {
    buttonRef?.classList.add('shake');
    setTimeout(() => {
      buttonRef?.classList.remove('shake');
    }, 1000);
  }

  const handleCartAdd = async (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    loading = true;
    try {
      await add(variantToCartItem(product));
      added = true;

      onProductAdded?.();

      setTimeout(() => {
        added = false;
      }, 1800);

      try {
        EVENTS.emit('cart_item_add', { variant: product, quantity: 1, ignoreDrawer: ignoreDrawer ?? false });
      } catch (e) {
        // Ignore error?
      }
    } catch (error) {
      console.error(error);
      outOfStock = true;
      showPopover = true;
      triggerButtonShake();
    } finally {
      loading = false;
    }
  }

  const locale = getLocaleFromUrl(page.url);
  const percentDiscount = $derived(calculatePercentDiscount(product.price));
</script>

<style>

  @keyframes shakeAnim {
    0% {
      transform: translateX(0);
    }
    20% {
      transform: translateX(-3px);
    }
    40% {
      transform: translateX(6px);
    }
    60% {
      transform: translateX(-6px);
    }
    80% {
      transform: translateX(3px);
    }
    100% {
      transform: translateX(0);
    }
  }

  .shake {
    position: relative;
    animation-name: shakeAnim;
    animation-duration: 0.39s;
    animation-iteration-count: once;
    animation-timing-function: ease-in;
  }
</style>

<a href={rewriteUrl(page, `/product/${product.handle}`)} style="width: 100%;" onclick={onclick}>
  <Card {...props} css={{ ...classes.root, ...cssProp }} spacing="tight" colorVariant="transparent">
    {#key product.handle}
            {#if percentDiscount > 0}
        <CampaignBadge css={float.raw({zIndex: 4, mt: 6, mr: 6})} colorVariant="campaign">
          {percentDiscount}%
        </CampaignBadge>
      {/if}

      <div style="position: relative">
      {#if product.isShortDated}
        <ShortDatedBadge
          size="xs"
          css={float.raw({
            placement: 'bottom-start',
            zIndex: 4,
            mb: 3,
            ml: 6
          })}
        />
      {/if}

      <CardImage
       css={{aspectRatio: `${imageAspectRatio}!`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
       aspectRatio={imageAspectRatio}
       src={product.images[0]?.url}
       width={imageWidth}
       background="{product.images[0]?.url}?width=8"
       alt={m.productImageAlt({ "productName": product.name, "brandName": product.brand.name })}
       fit="contain"
        >
        </CardImage>
      </div>
    {/key}
    <CardHeader css={classes.header}>
      <span class={css(classes.brandName)}>{product.brand.name}</span>
      <h3 class={css(classes.title, titleStyles)}>{product.name}</h3>
    </CardHeader>
    <CardContent css={classes.content}>
      {#if product.price.discount}
        <span class={css(classes.campaignPrice)}>{getLocaleFormattedPrice(product.price, locale).totalPrice}</span>
        <span class={css(classes.originalPrice)}>{getLocaleFormattedPrice(product.price, locale).originalPrice}</span>
      {:else}
        <span class={css(classes.price)}>{getLocaleFormattedPrice(product.price, locale).totalPrice}</span>
      {/if}
    </CardContent>
    <CardFooter css={classes.footer}>
      <span bind:this={buttonRef} class={css({width: '100%', display: 'flex', justifyContent: 'stretch'})}>
        <Button
          disabled={loading || added}
          variant={outOfStock ? 'cta' : added ? 'secondary' : 'primary'}
          size="sm"
          fullWidth
          onclick={(e) => {
            if (outOfStock) {
              e.preventDefault();
              e.stopPropagation();
              showBackInStockDialog = true;
            } else {
              handleCartAdd(e);
            }
          }}
        >
          {outOfStock ? m.notifyButtonLabel() : added ? m.addedShort() : buttonLabel}
          {#if loading}
            <Loader size="xs" />
          {:else if added}
            <Check style="bold">
              <title>{m.iconCheck()}</title>
            </Check>
          {:else if outOfStock}
            <Bell style="bold">
              <title>{m.getNotified()}</title>
            </Bell>
          {:else}
            <ShoppingCartSimple style="bold">
              <title>{m.iconShoppingCartAdd()}</title>
            </ShoppingCartSimple>
          {/if}
        </Button>
      </span>
    </CardFooter>
  </Card>
</a>

<BackInStockDialog
  open={showBackInStockDialog}
  product={product}
  onClose={() => showBackInStockDialog = false}
/>
